import React, { useContext } from 'react';
import gettext from '@eb/gettext';

import { EventCard, CONSUMER_CARD_DEFAULTS } from '@eb/event-renderer';
import { withMainControls } from '@eb/eds-structure';

import { DiscoveryEvent } from '../types';
import { UserContext } from '../context';
import { GA_ORGANIZER_CATEGORY } from '../constants';

const getLocationInfo = (venueName: string, venueAddress: string) => {
    //If we have both name and address add comma separated address
    if (venueAddress && venueName) {
        //ex. Eventbrite • San Francisco CA
        return `${venueName} \u2022 ${venueAddress}`;
    }

    return venueName || venueAddress;
};

const transformEventsForDisplay = (events: DiscoveryEvent[]) => {
    return events.map((ebEvent: DiscoveryEvent) => {
        let [startDate, startTime] = ebEvent.start.local.split('T');
        let subtitle;

        if (ebEvent.is_series_parent) {
            startDate = '';
            subtitle = gettext('MULTIPLE DATES');
        }

        return {
            subtitle,
            id: ebEvent.id,
            isFree: ebEvent.is_free,
            imageUrl: ebEvent?.logo?.url,
            name: ebEvent?.name.text,
            isOnlineEvent: ebEvent.online_event,
            formattedPriceString: ebEvent.is_free ? '' : ebEvent.price_range,
            startTime: startTime,
            startDate: ebEvent.hide_start_date ? undefined : startDate,
            timezome: ebEvent.start.timezone,
            url: ebEvent.url,
            locationInfo: getLocationInfo(
                ebEvent?.venue?.name,
                ebEvent?.venue?.address?.localized_area_display,
            ),
            savedByYou: ebEvent.bookmarked,
        };
    });
};

interface EventRendererProps {
    events: DiscoveryEvent[];
    removeSaveHandlers?: boolean;
}

export interface EventCardEventData {
    id: string;
    isFree: boolean;
    imageUrl: string;
    name: string;
    isOnlineEvent: boolean;
    startTime: string;
    timezome: string;
    url: string;
    locationInfo: string;
    savedByYou: boolean;
    startDate?: string;
    subtitle?: string;
    formattedPriceString?: string;
}

export const EventRenderer: React.FunctionComponent<EventRendererProps> = (
    props,
) => {
    const user = useContext(UserContext);
    const isAuthenticated = user?.isAuthenticated;

    if (!props.events || props.events.length === 0) {
        return (
            <div className="eds-text-bl">
                {gettext('Sorry, there are no upcoming events')}
            </div>
        );
    }

    const events = transformEventsForDisplay(props.events);

    return (
        <div>
            {events.map((eventData: EventCardEventData) => (
                <EventCard
                    {...CONSUMER_CARD_DEFAULTS}
                    gaCategory={GA_ORGANIZER_CATEGORY}
                    hideSaveButton={!!props.removeSaveHandlers}
                    isAuthenticated={!!isAuthenticated}
                    key={eventData.id}
                    type="list"
                    user={{ id: user?.publicId || '' }}
                    {...eventData}
                />
            ))}
        </div>
    );
};

export const EventRenderWithControls = withMainControls(EventRenderer);
